import type { ElementType, ReactNode } from "react";
import { cn } from "@/lib/utils";

type Politeness = "polite" | "assertive";

interface LiveStatusProps {
  /**
   * Message / content to announce. When the wrapper is kept mounted (the
   * default for status regions) screen readers announce changes to this
   * content as they happen.
   */
  children?: ReactNode;
  /**
   * "polite" (default) for non-urgent status updates (search counts, price
   * recalculation, loading). "assertive" for errors that should interrupt.
   */
  politeness?: Politeness;
  /**
   * Override the ARIA role. Defaults to "alert" when assertive (so newly
   * inserted error content is announced even if the region was not yet in the
   * DOM) and "status" when polite.
   */
  role?: "status" | "alert";
  /** Render the region visually hidden so it is announced to AT only. */
  srOnly?: boolean;
  className?: string;
  /** Element tag to render. Defaults to "p". */
  as?: ElementType;
  /** aria-atomic — read the whole message on change. Defaults to true. */
  atomic?: boolean;
  "data-testid"?: string;
}

/**
 * Reusable WCAG-compliant ARIA live region.
 *
 * Two usage patterns:
 *  - Visible inline status/error: wrap the visible text, e.g. an auth error
 *    `<LiveStatus politeness="assertive" className="text-red-600">{error}</LiveStatus>`.
 *  - Screen-reader-only announcement that mirrors a visual change: keep it
 *    mounted with `srOnly` and feed it the current state, e.g.
 *    `<LiveStatus srOnly>{count} cars found</LiveStatus>`.
 */
export function LiveStatus({
  children,
  politeness = "polite",
  role,
  srOnly = false,
  className,
  as: Tag = "p",
  atomic = true,
  ...rest
}: LiveStatusProps) {
  const resolvedRole = role ?? (politeness === "assertive" ? "alert" : "status");
  return (
    <Tag
      role={resolvedRole}
      aria-live={politeness}
      aria-atomic={atomic}
      className={cn(srOnly && "sr-only", className)}
      {...rest}
    >
      {children}
    </Tag>
  );
}
